import CommonContainer from "../Container/CommonContainer";
import React, { useEffect, useState } from 'react';
import CommonPagination from '../../Common/CommonPagination';
import { FaSearch, FaSpinner } from "react-icons/fa";
import * as PartnerActivityReportService from '../../Services/MHL_Reports/PartnerActivityReportService';
import moment from "moment";
import { Download, Loader2 } from 'lucide-react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Popover from '../../Common/Popper';

const PartnerActivityReportComponent = () => {
    const [partnerTrackingData, setPartnerTrackingData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;

    const [dateRangeOption, setDateRangeOption] = useState('TODAY');
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);

    const getPartnerTrackingData = (period) => {
        PartnerActivityReportService.PartnerActivityReport(period)
            .then(({ data }) => {
                if (data.IsSuccess) {
                    setPartnerTrackingData(data?.Data);
                } else {
                    setPartnerTrackingData([]);
                }
            })
            .catch(() => {
                setPartnerTrackingData([]);
            });
    };

    useEffect(() => {
        getPartnerTrackingData(dateRangeOption);
    }, [dateRangeOption]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const pivotedResult = React.useMemo(() => {
        if (!partnerTrackingData?.length) {
            return { eventNames: [], rows: [] };
        }

        const eventNames = [
            ...new Set(partnerTrackingData.map(e => e.Event_Name))
        ];

        const map = {};

        partnerTrackingData.forEach(row => {
            const key = `${row.Partner_Code}_${row.CreatedOn}`;

            if (!map[key]) {
                map[key] = {
                    Partner_Code: row.Partner_Code,
                    Partner_Name: row.Partner_Name,
                    Org_Sbu: row.Org_Sbu,
                    Primary_Org: row.Primary_Org,
                    CreatedOn: row.CreatedOn,
                };

                eventNames.forEach(ev => {
                    map[key][ev] = 0;
                });
            }

            map[key][row.Event_Name] = row.Event_Count;
        });

        return {
            eventNames,
            rows: Object.values(map),
        };
    }, [partnerTrackingData]);

    useEffect(() => {
        // //console.log('pivot', pivotedResult?.rows?.length)
        // //console.log('item', itemsPerPage)
    }, [itemsPerPage, pivotedResult])

    const filteredPivotRows = React.useMemo(() => {
        const lowerSearch = searchTerm.trim().toLowerCase();

        return pivotedResult.rows.filter(row => {
            return (
                row?.Partner_Code?.toLowerCase().includes(lowerSearch) ||
                row?.Partner_Name?.toLowerCase().includes(lowerSearch)
            );
        });
    }, [pivotedResult, searchTerm]);

    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;

    const currentPartnerTrackingData = React.useMemo(() => {
        return filteredPivotRows.slice(indexOfFirst, indexOfLast);
    }, [filteredPivotRows, indexOfFirst, indexOfLast]);

    const handleDownloadReport = () => {
        if (!pivotedResult.rows.length) return;

        const excelData = pivotedResult.rows.map(row => {
            const obj = {
                "Partner Code": row.Partner_Code,
                "Partner Name": row.Partner_Name,
                "Org Sbu": row.Org_Sbu,
                "Primary Org": row.Primary_Org,
                // "Created On": row.CreatedOn, // optional
            };

            pivotedResult.eventNames.forEach(event => {
                obj[event] = row[event] ?? 0;
            });

            return obj;
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Partner Activity");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], {
            type: "application/octet-stream",
        });

        saveAs(blob, `Partner_Activity_${dateRangeOption}.xlsx`);
    };

    const getShortName = (char, limit = 15) => {
        const full = `${char || ""}`.trim();
        if (!full) return "-";
        return full.length > limit ? full.substring(0, limit) + "..." : full;
    };
 

    return (
        <CommonContainer heading="Partner Activity Tracking">
            <div className="d-flex flex-column flex-lg-row mb-2 date-search-content gap-2 search-sm" style={{ justifyContent: "space-between" }}>
                <div className="d-flex align-items-center gap-2 custom-date-range">
                    <select
                        className="form-select date-range-select"
                        style={{}}
                        value={dateRangeOption}
                        onChange={(e) => setDateRangeOption(e.target.value)}
                    >
                        <option value="TODAY">Today</option>
                        <option value="LAST_7_DAYS">Last 7 Days</option>
                        <option value="LAST_30_DAYS">Last 30 Days</option>
                        <option value="MTD">MTD</option>
                    </select>
                    <div>
                        <button
                            className="btn custom-sumbitbtn d-flex align-items-center justify-content-center gap-2"
                            onClick={handleDownloadReport}
                            title="Download Report"
                        >
                            {isDownloadingReport ? (
                                <>
                                    <FaSpinner className="w-5 h-5 fa-spin" />
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <Download size={18} />
                                    Download Report
                                </>
                            )}
                        </button>
                    </div>
                </div>

                <div className="search-box" style={{ position: "relative" }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={handleSearch}
                        style={{ paddingRight: "35px", borderRadius: "5px", border: "1px solid #ddd" }}
                    />
                    <button
                        className="btn custom-sumbitbtn d-flex align-items-center justify-content-center search-box-btn"
                        style={{
                            padding: '5px',
                            position: "absolute",
                            right: "0px",
                            height: "35px",
                            width: "45px",
                            top: "50%",
                            borderTopLeftRadius: "0",
                            borderBottomLeftRadius: "0",
                            transform: "translateY(-45%)"
                        }}
                        onClick={() => setCurrentPage(1)}
                    >
                        <FaSearch />
                    </button>
                </div>
            </div>

            <div className="mt-3 mt-lg-2">
                <div className="table-responsive mhl-partner-activity-report-table">
                    <table className="table table-bordered custom-table">
                        <thead>
                            <tr>
                                <th>Partner Details</th>
                                <th>Org Sbu</th>
                                <th>Primary Org</th>
                                {/* <th>Partner Name</th> */}
                                {/* <th>Created On</th> */}

                                {pivotedResult?.eventNames?.map((event, idx) => (
                                    <th key={idx}>{event}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody style={{ fontSize: "10px" }}>
                            {currentPartnerTrackingData.length ? (
                                currentPartnerTrackingData.map((row, index) => (
                                    <tr key={index}>
                                        {/* <td>{row.Partner_Code}</td>
                                        <td>{row.Partner_Name ?? "--"}</td> */}
                                        {/* <td>{row.CreatedOn}</td> */}
                                        <td style={{ textAlign: "left", verticalAlign: "middle", paddingLeft: '15px' }}>
                                            <Popover
                                                content={
                                                    <div style={{ textAlign: "left" }}>
                                                        <p><strong>Partner Code:</strong> {row.Partner_Code}</p>
                                                        <p><strong>Partner Name:</strong> {row.Partner_Name}</p>
                                                    </div>
                                                }
                                            >

                                            <b>{getShortName(row.Partner_Code)}</b> - {getShortName(row.Partner_Name)}
                                            </Popover>

                                        </td>
                                        <td>{row.Org_Sbu}</td>
                                        <td>{row.Primary_Org}</td>

                                        {pivotedResult.eventNames.map((event, idx) => (
                                            <td key={idx}>{row[event]}</td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={pivotedResult.eventNames.length + 2} className="text-center">
                                        No data found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {filteredPivotRows?.length > itemsPerPage && (
                    <CommonPagination
                        pageList={filteredPivotRows}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        setCurrentPage={setCurrentPage}
                    />
                )}
            </div>
        </CommonContainer>
    )
}

export default PartnerActivityReportComponent